// Enums
export enum PrisonerStatus {
  ACTIVE = 'ACTIVE',
  RELEASED = 'RELEASED',
  TRANSFERRED = 'TRANSFERRED',
  DECEASED = 'DECEASED',
}

export enum CaseStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  SUSPENDED = 'SUSPENDED',
}

export enum ProtectionLevel {
  NORMAL = 'NORMAL',
  CONFIDENTIAL = 'CONFIDENTIAL',
  STRICTLY_CONFIDENTIAL = 'STRICTLY_CONFIDENTIAL',
}

export enum RecordItemStatus {
  DRAFT = 'DRAFT',
  PENDING_DISPOSITION = 'PENDING_DISPOSITION',
  FINALIZED = 'FINALIZED',
}

export enum DispositionAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  FINALIZE = 'FINALIZE',
  ARCHIVE = 'ARCHIVE',
}

export enum OcrStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum ModuleTag {
  VOLLZUGSVERLAUF = 'VOLLZUGSVERLAUF',
  VOLLZUGSPLANUNG = 'VOLLZUGSPLANUNG',
  DISZIPLINAR = 'DISZIPLINAR',
  LOCKERUNGEN = 'LOCKERUNGEN',
  BESUCHE = 'BESUCHE',
  DOKUMENTE = 'DOKUMENTE',
  NOTIZEN = 'NOTIZEN',
  VOLLSTRECKUNG = 'VOLLSTRECKUNG',
}

export enum RecordItemType {
  EVENT = 'EVENT',
  NOTE = 'NOTE',
  DOCUMENT = 'DOCUMENT',
  DISCIPLINARY_ACTION = 'DISCIPLINARY_ACTION',
  LEAVE_REQUEST = 'LEAVE_REQUEST',
  VISIT = 'VISIT',
  CORRESPONDENCE = 'CORRESPONDENCE',
}

export enum Needle1DocumentType {
  AUFNAHMESCHEIN = 'AUFNAHMESCHEIN',
  PERSONALBOGEN = 'PERSONALBOGEN',
  LICHTBILD = 'LICHTBILD',
  FINGERABDRUCK = 'FINGERABDRUCK',
  VOLLZUGSPLAN = 'VOLLZUGSPLAN',
  ENTLASSUNGSPAPIERE = 'ENTLASSUNGSPAPIERE',
  VERLEGUNGSURKUNDE = 'VERLEGUNGSURKUNDE',
}

// Interfaces
export interface Tenant {
  id: string;
  name: string;
  code: string;
  settings: Record<string, unknown>;
  createdAt: string;
}

export interface User {
  id: string;
  tenantId: string;
  keycloakId: string;
  username: string;
  email?: string;
  displayName?: string;
  enabled?: boolean;
  roles: string[];
  permissions?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminRole {
  id: string;
  name: string;
  description?: string | null;
  keycloakRoleName?: string | null;
  system: boolean;
  userCount: number;
  permissionKeys: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PermissionDefinition {
  id: string;
  key: string;
  description?: string | null;
  category: string;
}

export interface Prisoner {
  id: string;
  tenantId: string;
  prisonerNumber: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  admissionDate: string;
  releaseDate?: string;
  status: PrisonerStatus;
  protectionLevel: ProtectionLevel;
  photoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CriminalCase {
  id: string;
  prisonerId: string;
  tenantId: string;
  romanNumeral: string;
  caseReference?: string;
  court?: string;
  offense?: string;
  sentenceStart?: string;
  sentenceEnd?: string;
  status: CaseStatus;
  sequenceCounter: number;
  createdAt: string;
}

export interface NeedleAssignment {
  needle: 1 | 2 | 3;
  needle1DocType?: Needle1DocumentType;
  caseId?: string;
  caseSequenceNumber?: number;
  folioNumber?: number;
  bandNumber?: number;
}

export interface RecordItem {
  id: string;
  prisonerId: string;
  tenantId: string;
  type: RecordItemType;
  title: string;
  content?: string;
  modules: ModuleTag[];
  needle: 1 | 2 | 3;
  needle1DocType?: Needle1DocumentType;
  caseId?: string;
  caseSequenceNumber?: number;
  folioNumber?: number;
  bandNumber?: number;
  protectionLevel: ProtectionLevel;
  status: RecordItemStatus;
  createdById?: string;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string;
  documents?: Document[];
  dispositions?: Disposition[];
}

export interface Document {
  id: string;
  recordItemId: string;
  tenantId: string;
  filename: string;
  mimeType: string;
  s3Key: string;
  sizeBytes: number;
  pageCount?: number;
  currentVersionId?: string;
  ocrStatus: OcrStatus;
  ocrText?: string;
  createdAt: string;
}

export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  s3Key: string;
  hash: string;
  dispositionId: string;
  createdAt: string;
}

export interface Disposition {
  id: string;
  tenantId: string;
  recordItemId: string;
  action: DispositionAction;
  date: string;
  signedBy: string;
  signedByUser?: User;
  signature?: string;
  comment?: string;
  createdAt: string;
}

export interface AuditEvent {
  id: string;
  tenantId: string;
  userId: string;
  user?: User;
  action: string;
  objectType: string;
  objectId: string;
  beforeHash?: string;
  afterHash?: string;
  metadata: Record<string, unknown>;
  previousEventHash: string;
  eventHash: string;
  createdAt: string;
}

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

export type AiScope = 'documents' | 'prisoners' | 'both';

export interface AiConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiFilter {
  field: string;
  op: 'eq' | 'lt' | 'gt' | 'between' | 'in' | 'contains';
  value: unknown;
}

export interface AiQueryRequest {
  question: string;
  scope: AiScope;
  prisonerId?: string;
  caseId?: string;
  documentIds?: string[];
  filters?: AiFilter[];
  conversationHistory?: AiConversationMessage[];
}

export interface AiSource {
  type: 'document' | 'prisoner';
  id: string;
  meta?: Record<string, unknown>;
}

export interface AiStructuredPrisonerResult {
  id: string;
  prisonerNumber: string;
  firstName: string;
  lastName: string;
  city?: string | null;
  dateOfBirth?: string | null;
  releaseDate?: string | null;
  expectedEnd?: string | null;
}

export interface AiPrisonerResult {
  id: string;
  displayName: string;
  prisonerNumber?: string | null;
  wohnort?: string | null;
  entlassdatum?: string | null;
  anstalt?: string | null;
  abteilung?: string | null;
  aufnahmedatum?: string | null;
  geburtsdatum?: string | null;
  status?: string | null;
  meta?: Record<string, unknown>;
}

export interface AiDocumentResult {
  id: string;
  filename: string;
  prisonerId: string;
  prisonerNumber?: string | null;
  prisonerName?: string | null;
  recordItemId?: string | null;
  recordItemTitle?: string | null;
  recordItemType?: string | null;
  ocrStatus?: string | null;
  pageCount?: number | null;
  createdAt?: string | null;
}

export interface AiEntityLink {
  type: 'prisoner' | 'document' | 'recordItem';
  id: string;
  label: string;
  path: string;
  description?: string;
}

export interface AiVisualizationPoint {
  label: string;
  value: number;
}

export interface AiVisualization {
  chartType: 'bar' | 'pie' | 'stat';
  title: string;
  data: AiVisualizationPoint[];
}

export interface AiQueryResponse {
  answer: string;
  sources: AiSource[];
  resultType?: 'text' | 'prisoners' | 'documents' | 'mixed';
  prisonerResults?: AiPrisonerResult[];
  documentResults?: AiDocumentResult[];
  entityLinks?: AiEntityLink[];
  // Legacy compatibility while backend/frontend roll out.
  structuredResult?: AiStructuredPrisonerResult[];
  analysisMeta?: {
    appliedRules?: string[];
    totalCount?: number;
    grouping?: Array<{ key: string; count: number }>;
  };
  visualization?: AiVisualization;
}

export interface AiSettings {
  enableAiDocumentQa: boolean;
  enableAiPrisonerDataQa: boolean;
  enableAiHybridQa: boolean;
  enableAiPrisonerResultList?: boolean;
  provider: 'openai' | 'openrouter';
  openaiApiKeyConfigured: boolean;
  openaiApiKeyLastUpdatedAt?: string;
  openaiModel: string;
  openaiEmbeddingModel: string;
  apiBaseUrl?: string;
  allowDocumentScope: boolean;
  allowPrisonerScope: boolean;
}

export interface UpdateAiSettingsRequest {
  enableAiDocumentQa?: boolean;
  enableAiPrisonerDataQa?: boolean;
  enableAiHybridQa?: boolean;
  enableAiPrisonerResultList?: boolean;
  provider?: 'openai' | 'openrouter';
  openaiApiKey?: string;
  openaiModel?: string;
  openaiEmbeddingModel?: string;
  apiBaseUrl?: string;
  allowDocumentScope?: boolean;
  allowPrisonerScope?: boolean;
}

// Form types

// Nested DTOs for extended prisoner creation
export interface CreateIdentityDto {
  middleNames?: string;
  birthName?: string;
  birthPlace?: string;
  gender?: Gender;
  maritalStatus?: MaritalStatus;
  nationalities?: string[];
}

export interface CreateCustodyDto {
  custodyType: CustodyType;
  legalStatus?: LegalStatus;
  facilityName?: string;
  department?: string;
  station?: string;
  cellNumber?: string;
  securityLevel?: SecurityLevel;
  custodyStart?: string;
  expectedEnd?: string;
  courtName?: string;
  judgmentReference?: string;
  sentenceDuration?: string;
}

export interface CreateAddressDto {
  type?: AddressType;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface CreateEmergencyContactDto {
  firstName: string;
  lastName: string;
  relationship?: string;
  phone?: string;
  email?: string;
  address?: string;
}

export interface CreateBiometricsDto {
  heightCm?: number;
  eyeColor?: EyeColor;
  hairColor?: HairColor;
  distinguishingMarks?: string;
}

export interface CreateSocialDataDto {
  education?: string;
  profession?: string;
  languages?: string[];
  religion?: string;
  hasChildren?: boolean;
  numberOfChildren?: number;
  familyNotes?: string;
  residenceStatus?: string;
  countryOfOrigin?: string;
}

export interface CreatePrisonerDto {
  // Core fields
  prisonerNumber?: string; // Optional - auto-generated if not provided
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  admissionDate: string;
  releaseDate?: string;
  protectionLevel?: ProtectionLevel;
  
  // Extended data
  identity?: CreateIdentityDto;
  custody?: CreateCustodyDto;
  address?: CreateAddressDto;
  emergencyContact?: CreateEmergencyContactDto;
  biometrics?: CreateBiometricsDto;
  socialData?: CreateSocialDataDto;
}

export interface UpdatePrisonerDto {
  firstName?: string;
  lastName?: string;
  releaseDate?: string;
  status?: PrisonerStatus;
  protectionLevel?: ProtectionLevel;
}

export interface CreateRecordItemDto {
  type: RecordItemType;
  title: string;
  content?: string;
  modules: ModuleTag[];
  needle: 1 | 2 | 3;
  needle1DocType?: Needle1DocumentType;
  caseId?: string;
  protectionLevel?: ProtectionLevel;
  dispositionDate: string;
  dispositionComment?: string;
}

export interface SearchQuery {
  query: string;
  prisonerId?: string;
  modules?: ModuleTag[];
  needle?: 1 | 2 | 3;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

// ============================================
// STAMMDATEN (Master Data) Types
// ============================================

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  DIVERSE = 'DIVERSE',
  UNKNOWN = 'UNKNOWN',
}

export enum MaritalStatus {
  SINGLE = 'SINGLE',
  MARRIED = 'MARRIED',
  DIVORCED = 'DIVORCED',
  WIDOWED = 'WIDOWED',
  REGISTERED_PARTNERSHIP = 'REGISTERED_PARTNERSHIP',
  UNKNOWN = 'UNKNOWN',
}

export enum CustodyType {
  PRETRIAL = 'PRETRIAL',
  PRISON = 'PRISON',
  SUBSTITUTE = 'SUBSTITUTE',
  PREVENTIVE = 'PREVENTIVE',
  JUVENILE = 'JUVENILE',
  CIVIL = 'CIVIL',
  OTHER = 'OTHER',
}

export enum LegalStatus {
  PENDING = 'PENDING',
  FINAL = 'FINAL',
  PARTIALLY_FINAL = 'PARTIALLY_FINAL',
}

export enum SecurityLevel {
  STANDARD = 'STANDARD',
  ELEVATED = 'ELEVATED',
  HIGH = 'HIGH',
  MAXIMUM = 'MAXIMUM',
}

export enum AddressType {
  LAST_KNOWN = 'LAST_KNOWN',
  REGISTERED = 'REGISTERED',
  PREVIOUS = 'PREVIOUS',
}

export enum ContactType {
  EMERGENCY = 'EMERGENCY',
  FAMILY_MEMBER = 'FAMILY_MEMBER',
  LEGAL_GUARDIAN = 'LEGAL_GUARDIAN',
  LAWYER = 'LAWYER',
  OTHER = 'OTHER',
}

export enum IdentifierType {
  JUSTICE_ID = 'JUSTICE_ID',
  EXECUTION_NUMBER = 'EXECUTION_NUMBER',
  PREVIOUS_PRISONER_NUMBER = 'PREVIOUS_PRISONER_NUMBER',
  EXTERNAL_REFERENCE = 'EXTERNAL_REFERENCE',
}

export enum EyeColor {
  BLUE = 'BLUE',
  GREEN = 'GREEN',
  BROWN = 'BROWN',
  HAZEL = 'HAZEL',
  GRAY = 'GRAY',
  BLACK = 'BLACK',
  OTHER = 'OTHER',
}

export enum HairColor {
  BLONDE = 'BLONDE',
  BROWN = 'BROWN',
  BLACK = 'BLACK',
  RED = 'RED',
  GRAY = 'GRAY',
  WHITE = 'WHITE',
  BALD = 'BALD',
  OTHER = 'OTHER',
}

export enum SecurityFlagType {
  ESCAPE_RISK = 'ESCAPE_RISK',
  VIOLENCE_RISK = 'VIOLENCE_RISK',
  SUICIDE_RISK = 'SUICIDE_RISK',
  GANG_AFFILIATION = 'GANG_AFFILIATION',
  MEDIA_ATTENTION = 'MEDIA_ATTENTION',
  VIP = 'VIP',
  WITNESS_PROTECTION = 'WITNESS_PROTECTION',
  SEPARATION_REQUIRED = 'SEPARATION_REQUIRED',
}

// Master Data Interfaces
export interface PrisonerIdentity {
  id: string;
  prisonerId: string;
  middleNames?: string | null;
  birthName?: string | null;
  birthPlace?: string | null;
  gender: Gender;
  maritalStatus: MaritalStatus;
  nationalities: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerIdentifier {
  id: string;
  prisonerId: string;
  type: IdentifierType;
  value: string;
  issuingAuthority?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerBiometrics {
  id: string;
  prisonerId: string;
  heightCm?: number | null;
  eyeColor?: EyeColor | null;
  hairColor?: HairColor | null;
  distinguishingMarks?: string | null;
  fingerprintReference?: string | null;
  fingerprintAvailable: boolean;
  protectionLevel: ProtectionLevel;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerAddress {
  id: string;
  prisonerId: string;
  type: AddressType;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  isCurrent: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerContact {
  id: string;
  prisonerId: string;
  type: ContactType;
  firstName: string;
  lastName: string;
  relationship?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  isEmergencyContact: boolean;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerCustodyStatus {
  id: string;
  prisonerId: string;
  custodyType: CustodyType;
  legalStatus?: LegalStatus | null;
  facilityName?: string | null;
  department?: string | null;
  station?: string | null;
  cellNumber?: string | null;
  securityLevel: SecurityLevel;
  custodyStart?: string | null;
  expectedEnd?: string | null;
  courtName?: string | null;
  judgmentReference?: string | null;
  sentenceDuration?: string | null;
  remainingSentence?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerCustodyHistory {
  id: string;
  prisonerId: string;
  facilityName: string;
  department?: string | null;
  station?: string | null;
  cellNumber?: string | null;
  fromDate: string;
  toDate?: string | null;
  reason?: string | null;
  createdAt: string;
}

export interface PrisonerSecurityFlag {
  id: string;
  prisonerId: string;
  flagType: SecurityFlagType;
  severity: number;
  isActive: boolean;
  setAt: string;
  setBy?: string | null;
  clearedAt?: string | null;
  clearedBy?: string | null;
  reason?: string | null;
  protectionLevel: ProtectionLevel;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerSocialData {
  id: string;
  prisonerId: string;
  education?: string | null;
  profession?: string | null;
  languages: string[];
  religion?: string | null;
  hasChildren?: boolean | null;
  numberOfChildren?: number | null;
  familyNotes?: string | null;
  residenceStatus?: string | null;
  countryOfOrigin?: string | null;
  protectionLevel: ProtectionLevel;
  createdAt: string;
  updatedAt: string;
}

export interface PrisonerPhoto {
  id: string;
  prisonerId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  isPrimary: boolean;
  takenAt?: string | null;
  description?: string | null;
  uploadedBy: string;
  createdAt: string;
  downloadUrl?: string;
}

export interface ProfilePermissions {
  canEditBasic: boolean;
  canViewBiometrics: boolean;
  canEditBiometrics: boolean;
  canViewSecurity: boolean;
  canEditSecurity: boolean;
  canViewSensitiveSocialData: boolean;
  canEditSocialData: boolean;
  canUploadPhotos: boolean;
}

export interface SecuritySummary {
  activeCount: number;
  maxSeverity: number;
  activeFlags: PrisonerSecurityFlag[];
  hasCriticalFlags: boolean;
}

export interface PrisonerProfile {
  core: Prisoner;
  identity?: PrisonerIdentity | null;
  identifiers: PrisonerIdentifier[];
  biometrics?: PrisonerBiometrics | null;
  addresses: PrisonerAddress[];
  contacts: PrisonerContact[];
  custodyStatus?: PrisonerCustodyStatus | null;
  custodyHistory: PrisonerCustodyHistory[];
  security?: SecuritySummary | null;
  socialData?: PrisonerSocialData | null;
  photos: PrisonerPhoto[];
  primaryPhoto?: PrisonerPhoto | null;
  permissions: ProfilePermissions;
}
